import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ArticleListItem } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { decodeEntities, stripHtml } from "@/lib/html";
import {
  avatarTone,
  channelIcon,
  channelNameOf,
  dominantChannel,
  emailFromAddress,
  formatFromAddress,
  formatToAddresses,
  initialsFor,
  senderDisplayName,
} from "@/lib/articleChannel";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { useShowAvatars } from "@/lib/showAvatars";
import { AiOriginMarker, AiOriginToggle, AiOriginTrace } from "./AiOriginBadge";
import { ArticleSecurityBadge } from "./ArticleSecurityBadge";
import { useArticleSecurity } from "./useArticleSecurity";
import { useAiOriginTrace } from "./useAiOriginTrace";
import { useTicketReplyDrafts } from "@/lib/replyDrafts";
import { ArticleQuickActions } from "./ArticleQuickActions";
import { DraftListRow } from "./DraftPlaceholder";
import { ArticleBodyLoader, AttachmentList } from "./ArticleTimeline";
import { SummaryMarker } from "./SummaryMarker";
import { useSummaryBoundary } from "./useSummaryBoundary";
import type { ArticleListState } from "./useArticleListState";

/**
 * Article list "Variante 3 — Liste + Lesebereich": a master-detail split.
 * All filter/sort/selection state lives in `useArticleListState` (shared
 * with the conversation view via the ArticleMasterDetail orchestrator) —
 * this component is purely presentational over that state.
 */
export function ArticleSplitView({
  ticketId,
  canNote,
  canDelete = false,
  locale,
  state,
}: {
  ticketId: number;
  canNote: boolean;
  /** Whether the agent may delete internal notes (``rw`` permission). */
  canDelete?: boolean;
  locale: string;
  state: ArticleListState;
}) {
  const { t } = useTranslation();
  const { articles, sorted, selectedId, setSelectedId, selected, onListKeyDown, descending } = state;
  const { boundaryId, createdAt } = useSummaryBoundary(ticketId, sorted);
  const drafts = useTicketReplyDrafts(ticketId);
  // A draft's reply target may have been filtered out of `sorted` (e.g. the
  // "Notes" filter while replying to an email) — look it up in the full,
  // unfiltered article set, falling back to the ticket's dominant channel so
  // the composer still opens in a sensible mode.
  const draftChannelName = (articleId: number): string | undefined => {
    const target = articles.find((a) => a.id === articleId);
    return target ? channelNameOf(target) : (dominantChannel(articles) ?? undefined);
  };

  if (sorted.length === 0) {
    return <p className="text-sm text-muted">{t("ticket.noArticles")}</p>;
  }

  return (
    <div className="flex flex-col gap-3 md:flex-row" data-testid="article-master-detail">
      <div
        role="listbox"
        aria-label={t("ticket.articles")}
        tabIndex={0}
        onKeyDown={onListKeyDown}
        data-testid="article-list"
        className="max-h-[40vh] shrink-0 space-y-1 overflow-y-auto rounded-lg border border-hairline bg-surface p-1.5 md:max-h-none md:w-[300px]"
      >
        {/* Unsent drafts pin to the top in either sort direction — they are
            the most current thing on the ticket, and burying one below the
            fold would defeat the point of showing it at all. */}
        {drafts.map((d) => (
          <DraftListRow
            key={`draft-${d.articleId}`}
            draft={d}
            locale={locale}
            channelName={draftChannelName(d.articleId)}
          />
        ))}
        {sorted.map((a) => (
          <Fragment key={a.id}>
            {/* Marker sits between summarized and newer articles — above the
                boundary row in newest-first order, below it in oldest-first. */}
            {descending && a.id === boundaryId && (
              <SummaryMarker createdAt={createdAt} locale={locale} />
            )}
            <ArticleListRow
              ticketId={ticketId}
              article={a}
              locale={locale}
              selected={a.id === selectedId}
              onSelect={() => setSelectedId(a.id)}
            />
            {!descending && a.id === boundaryId && (
              <SummaryMarker createdAt={createdAt} locale={locale} />
            )}
          </Fragment>
        ))}
      </div>
      <div className="min-w-0 flex-1" data-testid="article-reader">
        {selected ? (
          <ArticleReader
            ticketId={ticketId}
            article={selected}
            canNote={canNote}
            canDelete={canDelete}
            locale={locale}
          />
        ) : (
          <p className="text-sm text-muted">{t("ticket.noArticles")}</p>
        )}
      </div>
    </div>
  );
}

function ArticleListRow({
  ticketId,
  article,
  locale,
  selected,
  onSelect,
}: {
  ticketId: number;
  article: ArticleListItem;
  locale: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const showAvatars = useShowAvatars();
  const senderName = senderDisplayName(article.from_address) || t("ticket.unknownSender");
  return (
    <div
      role="option"
      aria-selected={selected}
      data-testid={`article-list-item-${article.id}`}
      onClick={onSelect}
      className={cn(
        "flex cursor-pointer gap-2 rounded-md border-l-2 px-2 py-1.5",
        selected ? "border-l-accent bg-accent/10" : "border-l-transparent hover:bg-surface-subtle",
      )}
    >
      <Avatar
        initials={initialsFor(article)}
        email={showAvatars ? emailFromAddress(article.from_address) : null}
        tone={avatarTone(article.sender_type)}
        size={24}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              article.is_visible_for_customer ? "bg-green" : "bg-escalation",
            )}
            title={
              article.is_visible_for_customer
                ? t("ticket.articleVisibleTooltip")
                : t("ticket.articleInternalTooltip")
            }
          />
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-ink">{senderName}</span>
          {article.ai_origin && <AiOriginMarker articleId={article.id} />}
          <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted">
            {formatDateTime(article.create_time, locale)}
          </span>
        </div>
        <p className="truncate text-[11px] text-muted">
          <span aria-hidden>{channelIcon(channelNameOf(article))}</span>{" "}
          <ArticlePreview ticketId={ticketId} article={article} />
        </p>
      </div>
    </div>
  );
}

/** Fetches the article body (same query key as `ArticleBodyLoader`, so
 * opening it in the reading pane afterwards reuses the cached response) and
 * shows a stripped ~80-char plain-text preview. */
function ArticlePreview({ ticketId, article }: { ticketId: number; article: ArticleListItem }) {
  const { t } = useTranslation();
  const bodyQ = useQuery({
    queryKey: ["tickets", ticketId, "articles", article.id, "body"],
    queryFn: () => api.getArticleBody(ticketId, article.id),
  });
  if (bodyQ.isLoading) return <span>…</span>;
  if (!bodyQ.data) return <span>{t("ticket.noSubject")}</span>;
  const plain = bodyQ.data.is_html ? stripHtml(bodyQ.data.body) : decodeEntities(bodyQ.data.body);
  return <span>{plain.slice(0, 80) || t("ticket.noSubject")}</span>;
}

function ArticleReader({
  ticketId,
  article,
  canNote,
  canDelete,
  locale,
}: {
  ticketId: number;
  article: ArticleListItem;
  canNote: boolean;
  canDelete: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  const aiOrigin = useAiOriginTrace({ ticketId, articleId: article.id });
  const security = useArticleSecurity(ticketId, article);
  return (
    <div className="space-y-3 rounded-lg border border-hairline bg-surface p-3">
      <div className="space-y-1.5 border-b border-hairline pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">
              {article.subject || t("ticket.noSubject")}
            </p>
            <p className="truncate text-xs text-muted">
              {formatFromAddress(article.from_address)}
              {article.to_address ? ` → ${formatToAddresses(article.to_address)}` : ""}
            </p>
          </div>
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted" title={t("ticket.readerDetails")}>
            {formatDateTime(article.create_time, locale)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={article.is_visible_for_customer ? "success" : "muted"}>
            {article.is_visible_for_customer ? t("ticket.visibleCustomer") : t("ticket.internal")}
          </Badge>
          <Badge tone="default">{channelIcon(channelNameOf(article))}</Badge>
          {article.ai_origin && (
            <AiOriginToggle
              articleId={article.id}
              open={aiOrigin.open}
              onToggle={aiOrigin.toggle}
            />
          )}
          {security && <ArticleSecurityBadge articleId={article.id} security={security} />}
        </div>
      </div>
      {article.ai_origin && (
        <AiOriginTrace articleId={article.id} open={aiOrigin.open} query={aiOrigin.query} />
      )}
      <ArticleQuickActions
        ticketId={ticketId}
        article={article}
        canNote={canNote}
        canDelete={canDelete}
        replyTestId="article-reader-reply"
      />
      <ArticleBodyLoader ticketId={ticketId} articleId={article.id} />
      <AttachmentList ticketId={ticketId} articleId={article.id} />
    </div>
  );
}
